import { type FC, useState } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"

import { useFileUrl } from "@forento/shared-client/hooks/useFileUrl"
import { removeArrayIndex, replaceArrayValue } from "@forento/shared/utilities/array"
import { getDataUrlByFile, getExtensionByFileName, supportedFileExtensions } from "@forento/shared/utilities/file"
import { parseNumber } from "@forento/shared/utilities/number"

import Button, { SubmitButton } from "~/themes/school/components/Button"
import Dropdown from "~/themes/school/components/Dropdown"
import { DeleteIcon, UploadFileIcon, UploadIcon } from "~/themes/school/components/Icon"
import InputField, { InputLabel, useRichTextArea } from "~/themes/school/components/InputField"
import Modal, { ModalButtons, ModalTitle } from "~/themes/school/components/Modal"
import { useTranslation } from "~/translations"
import { dangerColor } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

type Props = {
	channel: { id: number | null; name: string }
	isOpen: boolean
	onClose(): void
	onCreate(): Promise<unknown>
}
const CreatePostModal: FC<Props> = ({ channel, isOpen, onClose, onCreate }) => {
	const t = useTranslation()

	const categories = query.community.listPostCategories.useQuery()

	const [title, setTitle] = useState("")
	const content = useRichTextArea({ label: t("general.text") })
	const [images, setImages] = useState<File[]>([])
	const [categoryId, setCategoryId] = useState<string | null>(null)
	const [pollOptions, setPollOptions] = useState<string[]>([])
	const [attachments, setAttachments] = useState<{ label: string; file: File }[]>([])
	const [isSubmitting, setSubmitting] = useState(false)

	const imagesDropzone = useDropzone({
		accept: Object.fromEntries(supportedFileExtensions.image.map(x => [`image/${x.toLowerCase()}`, []])),
		onDrop: files => setImages(current => [...current, ...files]),
	})
	const attachmentsDropzone = useDropzone({
		onDrop: async files => {
			for (const file of files) {
				const dialog = prompt(t("community.post.addAttachment"), file.name)
				if (dialog === null) continue
				setAttachments(current => [...current, { label: dialog, file }])
			}
		},
	})

	const handleSubmit = async () => {
		const exportedContent = content.exportEditorState()
		if (title.trim().length === 0 || exportedContent === null) return

		setSubmitting(true)

		try {
			await trpc.community.createPost.mutate({
				channelId: channel.id,
				title: title.trim(),
				content: exportedContent,
				imageDataUrls: await Promise.all(images.map(async image => await getDataUrlByFile(image))),
				categoryId: parseNumber(categoryId),
				pollOptions,
				attachments: await Promise.all(
					attachments.map(async attachment => ({
						label: attachment.label,
						dataUrl: await getDataUrlByFile(attachment.file),
						extension: getExtensionByFileName(attachment.file.name) ?? "",
					})),
				),
			})
			await onCreate()

			setTitle("")
			content.set(null)
			setCategoryId(null)
			setPollOptions([])
			setAttachments([])
			onClose()
		} catch (error) {
			console.error(error)
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal isOpen={isOpen} onSubmit={handleSubmit} width={800}>
			<ModalTitle>{t("community.post.create")}</ModalTitle>
			<InputField label={t("general.title")} value={title} onChange={setTitle} />
			{content.element}
			<div>
				<InputLabel>{t("community.post.images")}</InputLabel>
				<div className="flex flex-wrap gap-4">
					{images.map((image, index) => (
						<UploadedImage
							key={index}
							image={image}
							onDelete={() => setImages(current => removeArrayIndex(current, index))}
						/>
					))}
					<div
						{...imagesDropzone.getRootProps()}
						className="flex cursor-pointer items-center justify-center rounded-lg border border-primary p-8"
					>
						<input {...imagesDropzone.getInputProps()} />
						<UploadIcon className="h-6 w-6 text-primary" />
					</div>
				</div>
			</div>
			<Dropdown
				label={t("community.post.category")}
				items={[
					{ id: "none", title: t("community.post.noCategory") },
					...(categories.data?.map(category => ({
						id: category.id.toString(),
						title: category.label,
					})) ?? []),
					{ id: "create", title: t("community.post.createCategory") },
				]}
				selectedItemId={categoryId ?? "none"}
				onChange={id => setCategoryId(id === "none" ? null : id)}
			/>
			<PollOptions>
				{pollOptions.map((option, index) => (
					<PollOption key={index}>
						<PollOptionInput
							value={option}
							onChange={value => setPollOptions(current => replaceArrayValue(current, index, value))}
						/>
						<PollOptionDeleteButton
							onClick={() => setPollOptions(current => removeArrayIndex(current, index))}
						>
							<DeleteIcon />
						</PollOptionDeleteButton>
					</PollOption>
				))}
				<Button variant="secondary" onClick={() => setPollOptions(current => [...current, ""])}>
					{t("community.post.addPollOption")}
				</Button>
			</PollOptions>
			<div>
				<InputLabel>{t("community.post.attachments")}</InputLabel>
				<div className="flex flex-wrap gap-4">
					{attachments.map((attachment, index) => (
						<div
							key={index}
							className="relative flex items-center justify-center rounded-lg border border-primary p-8 break-all"
						>
							<button
								type="button"
								className="absolute top-1 right-1 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
								onClick={() => setAttachments(current => removeArrayIndex(current, index))}
							>
								<DeleteIcon className="h-4 w-4 text-danger" />
							</button>
							{attachment.label}
						</div>
					))}
					<div
						{...attachmentsDropzone.getRootProps()}
						className="flex cursor-pointer items-center justify-center rounded-lg border border-primary p-8"
					>
						<input {...attachmentsDropzone.getInputProps()} />
						<UploadFileIcon className="h-6 w-6 text-primary" />
					</div>
				</div>
			</div>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose} isDisabled={isSubmitting}>
					{t("cancel")}
				</Button>
				<SubmitButton variant="primary" isLoading={isSubmitting}>
					{t("community.post.postToChannel", { channelName: channel.name })}
				</SubmitButton>
			</ModalButtons>
		</Modal>
	)
}

const UploadedImage: FC<{ image: File; onDelete(): void }> = ({ image, onDelete }) => {
	const imageUrl = useFileUrl(image)

	return (
		<div className="relative">
			<img src={imageUrl} className="h-24 rounded-lg" />
			<button
				type="button"
				className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
				onClick={onDelete}
			>
				<DeleteIcon className="h-4 w-4 text-danger" />
			</button>
		</div>
	)
}

const PollOptions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const PollOption = styled.div`
	display: flex;
	gap: 16px;
`

const PollOptionInput = styled(InputField)`
	flex: 1;
`

const PollOptionDeleteButton = styled(Button)`
	width: 52px;
	height: 52px;
	background-color: ${dangerColor};
	color: white;
	padding: 8px;
	border-radius: 4px;
`

export default CreatePostModal
